import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { conversationService } from '../services/conversationService';
import { useConversationStore, useUIStore } from '../../../stores';

const UNSET = Symbol('unset');

/**
 * Keeps the address bar and the selected conversation in step, so every
 * conversation has its own link: `/conversations/:sessionId`. Email alerts,
 * the AI-escalation toast and Clerk facts link there, and staff can copy,
 * refresh or bookmark it. `/dashboard` means no conversation is open.
 *
 * Whichever side changed wins. A new URL (an opened link, back/forward) opens
 * that conversation, fetching it first when it isn't in the loaded list (an
 * older resolved one, say). A new selection (clicking a row) pushes its URL.
 */
export function useConversationRoute() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const selectedId = useConversationStore((s) => s.selectedConversationId);

  const lastUrlId = useRef<string | undefined>(undefined);
  const lastSelectedId = useRef<string | null | typeof UNSET>(UNSET);

  useEffect(() => {
    const urlChanged = sessionId !== lastUrlId.current;
    const selectionChanged = selectedId !== lastSelectedId.current;
    const firstRun = lastSelectedId.current === UNSET;
    lastUrlId.current = sessionId;
    lastSelectedId.current = selectedId;

    const open = async (id: string) => {
      if (!useConversationStore.getState().getConversation(id)) {
        try {
          const conversation = await conversationService.getConversation(id);
          if (!useConversationStore.getState().getConversation(conversation.id)) {
            useConversationStore.getState().addConversation(conversation);
          }
        } catch {
          if (lastUrlId.current === id) {
            toast.error('That conversation could not be found.');
            navigate('/dashboard', { replace: true });
          }
          return;
        }
        // Staff moved on (clicked another row, went back) while it loaded.
        if (lastUrlId.current !== id) return;
      }
      useConversationStore.getState().selectConversation(id);
      useUIStore.getState().setMobileView('chat');
    };

    if (urlChanged) {
      if (!sessionId) {
        useConversationStore.getState().selectConversation(null);
      } else if (sessionId !== selectedId) {
        open(sessionId);
      }
      return;
    }

    if (selectionChanged && selectedId !== (sessionId ?? null)) {
      // Clearing the selection, or restoring the one left open before visiting
      // another page, shouldn't add a history entry.
      navigate(selectedId ? `/conversations/${selectedId}` : '/dashboard', {
        replace: !selectedId || firstRun,
      });
    }
  }, [sessionId, selectedId, navigate]);
}
